import { defineStore } from "pinia";
import { UserAttributes } from "common/userTypes";
import { ref } from "vue";
import apiHelper from "../../helpers/apiHelper";
import { isStudentPresent } from "../../helpers/studentPresence";

export const useStudentStore = defineStore("studentanswer", () => {
  let students = ref(<Map<string, UserAttributes>>new Map());

  let authorizedStudentUUId = ref<string | null>(null);

  function getStudents(now = Date.now()) {
    return Array.from(students.value.values()).filter((student) =>
      isStudentPresent(student.lastHeartbeatTime, now),
    );
  }

  function getAuthorizedStudentUUId() {
    return authorizedStudentUUId.value;
  }

  function noteStudentHeartbeat(user: UserAttributes, at = new Date()) {
    const existing = students.value.get(user.uuid);
    const next = existing ? { ...existing } : { ...user };
    next.lastHeartbeatTime = at;
    students.value.set(user.uuid, next);
  }

  async function setStudentHeartbeat(userUUId: string) {
    if (!students.value.get(userUUId)) {
      const db = apiHelper();
      const user = await db.getUser(userUUId);
      if (!user?.uuid) return;
      students.value.set(user.uuid, user);
    }
    const student = students.value.get(userUUId);
    if (!student) return;
    noteStudentHeartbeat(student, new Date());
  }

  function removeStudent(userUUId: string) {
    students.value.delete(userUUId);
  }

  function clearStudents() {
    students.value.clear();
  }

  function pruneStaleStudents(now = Date.now()): string[] {
    const removed: string[] = [];
    for (const student of [...students.value.values()]) {
      if (!isStudentPresent(student.lastHeartbeatTime, now)) {
        students.value.delete(student.uuid);
        removed.push(student.uuid);
      }
    }
    return removed;
  }

  function setAuthorizedStudentUUId(newAuthorizedStudentUUId: string | null) {
    authorizedStudentUUId.value = newAuthorizedStudentUUId;
  }

  return {
    getStudents,
    getAuthorizedStudentUUId,
    noteStudentHeartbeat,
    setStudentHeartbeat,
    removeStudent,
    clearStudents,
    pruneStaleStudents,
    setAuthorizedStudentUUId,
  };
});
